/**
 * formSource.ts — attribution stamp every public form posts with its payload.
 *
 * Slack alerts used to be titled after the offer the form sold, which drifts
 * as copy changes and never said which page the visitor was actually on. Each
 * form now calls formSource("<name>") and sends the result as `formSource`;
 * the server normalizes it (server/form-source.ts) and drops "Form" and "Page"
 * into the notification.
 *
 * Keep the field names in sync with FormSource in server/form-source.ts.
 */

export interface FormSourcePayload {
  /** Human name of the form, e.g. "Talk to a Strategist". */
  formName: string;
  /** Where on the page the form sat, for pages that carry several. */
  placement?: string;
  /** Full URL of the page the form was submitted from. */
  pageUrl?: string;
  /** Path + query of that page, for a short Slack label. */
  pagePath?: string;
  /** Where the visitor arrived from, when the browser tells us. */
  referrer?: string;
}

/**
 * Snapshot the current page for a form submission. Safe during SSR/prerender —
 * without a window it returns the name alone and the server falls back to the
 * Referer header.
 */
export function formSource(formName: string, placement?: string): FormSourcePayload {
  const base: FormSourcePayload = { formName };
  if (placement) base.placement = placement;
  if (typeof window === "undefined") return base;

  const { href, pathname, search } = window.location;
  base.pageUrl = href;
  base.pagePath = `${pathname}${search}`;
  const referrer = typeof document !== "undefined" ? document.referrer : "";
  if (referrer) base.referrer = referrer;
  return base;
}
